// ⚠️ MIRROR of auth-service/src/utils/plans.js. Keep the two in step.
//
// It exists because the browser has to draw the plan cards before it has spoken
// to the auth service — the same reason lib/leadDays.js and lib/workload.js
// mirror their server halves. The server is always the enforcer: nothing here
// decides whether a firm may do anything.
//
// ⚠️ PRICES ARE PLACEHOLDERS and display-only. Change them in the server file
// AND here. Nothing in this codebase takes payment.

export const PLANS = [
  {
    // ⚠️ SELF-SERVE — the only plan that is granted the moment it is asked for,
    // with no super-admin decision. The server does that (utils/subscription.js);
    // this flag only lets the card say so and the button read "Start free" rather
    // than "Request approval", which would promise a wait that never happens.
    id: 'free',
    name: 'Free',
    tagline: 'Try the platform with a few clients',
    minSeats: 1,
    maxSeats: 2,
    priceMonthly: 0,
    maxClients: 5,
    maxFilesPerClient: 10,
    maxStaffRoleLevel: 1,
    selfServe: true,
    features: [
      'Your Admin plus one Associate',
      'Up to 5 clients',
      'Up to 10 files per client',
      'Compliance calendar, tasks and attendance',
    ],
  },
  {
    id: 'small',
    name: 'Small',
    tagline: 'A partner and a small team',
    minSeats: 1,
    maxSeats: 5,
    priceMonthly: 2999,
    maxClients: null,
    maxFilesPerClient: null,
    maxStaffRoleLevel: null,
    features: [
      'Up to 5 users',
      'Clients, compliance calendar and tasks',
      'Attendance and leave',
      'Client portal and chat',
    ],
  },
  {
    id: 'growing',
    name: 'Growing',
    tagline: 'A practice with articles and staff',
    minSeats: 6,
    maxSeats: 30,
    priceMonthly: 7999,
    maxClients: null,
    maxFilesPerClient: null,
    maxStaffRoleLevel: null,
    features: [
      'Up to 30 users',
      'Everything in Small',
      'Finance — invoicing, receipts and ageing',
      'Reports and the work-done register',
    ],
  },
  {
    id: 'large',
    name: 'Large',
    tagline: 'A multi-partner firm',
    minSeats: 31,
    // ⚠️ null is UNLIMITED, not zero — read it with `== null`, never a falsy
    // check, or the biggest plan renders as the most restrictive.
    maxSeats: null,
    priceMonthly: 19999,
    maxClients: null,
    maxFilesPerClient: null,
    maxStaffRoleLevel: null,
    features: [
      'Unlimited users',
      'Everything in Growing',
      'Document store with S3 + OneDrive mirror',
      'Priority support',
    ],
  },
];

export const planById = (id) => PLANS.find((p) => p.id === id) || null;

export const planName = (id) => planById(id)?.name || '—';

export function seatRangeLabel(planId) {
  const plan = planById(planId);
  if (!plan) return '';
  if (plan.maxSeats == null) return `${plan.minSeats}+ users`;
  if (plan.minSeats === 1) return `up to ${plan.maxSeats} users`;
  return `${plan.minSeats} – ${plan.maxSeats} users`;
}

/**
 * The next plan up from `planId`, or null if there isn't one.
 *
 * Used to name the upgrade rather than offering a bare "ask for more" — an
 * admin who has just been refused a sixth user wants to know what they would be
 * moving to, not to go and work it out from a pricing table.
 */
export function nextPlanUp(planId) {
  const i = PLANS.findIndex((p) => p.id === planId);
  return i >= 0 && i < PLANS.length - 1 ? PLANS[i + 1] : null;
}

/** Does this headcount fall inside the plan's band? */
export function seatsFitPlan(seats, planId) {
  const plan = planById(planId);
  if (!plan) return false;
  const n = Number(seats);
  if (!Number.isFinite(n) || seats === '' || seats == null) return false;
  // ⚠️ `maxSeats == null` is UNLIMITED — anything at or above the floor fits.
  return n >= plan.minSeats && (plan.maxSeats == null || n <= plan.maxSeats);
}

/**
 * The seat figure to show once `planId` is picked.
 *
 * ⚠️ The request form used to prefill this from the firm's CURRENT headcount and
 * then leave it alone, so choosing a bigger plan produced requests reading
 * "the Large plan (5 users)" — a sentence whose two halves contradict each
 * other, since Large starts at 31. A number that already fits the chosen band is
 * the user's and is kept; one that cannot belong to it is replaced by the band's
 * floor rather than silently sent as-is.
 */
export function rebaseSeatsForPlan(seats, planId) {
  const plan = planById(planId);
  if (!plan) return seats;
  return seatsFitPlan(seats, planId) ? seats : String(plan.minSeats);
}

/**
 * ⚠️ Searches the PAID plans only. Free's 1–2 band sits inside Small's 1–5, so a
 * plain scan would answer "Free" for every firm of one or two — and this is the
 * function that draws the "Fits N users" chip and prefills the request form.
 * Free is something a firm chooses, never something its size implies.
 */
export const PAID_PLANS = PLANS.filter((p) => !p.selfServe);

export const isSelfServe = (id) => !!planById(id)?.selfServe;

export function planForSeats(seats) {
  const n = Number(seats) || 0;
  return (
    PAID_PLANS.find((p) => n >= p.minSeats && (p.maxSeats == null || n <= p.maxSeats)) ||
    PAID_PLANS[PAID_PLANS.length - 1]
  );
}

// Zero reads as "Free", not "₹0" — a price of nothing is a tier, and the word
// is what the card is actually offering.
export const formatPrice = (rupees) =>
  Number(rupees || 0) === 0 ? 'Free' : `₹${Number(rupees).toLocaleString('en-IN')}`;

// What the firm is told about each state. The words matter: a firm staring at a
// locked platform needs to know whether to wait, to act, or to call somebody.
export const STATUS_COPY = {
  none: {
    label: 'No subscription',
    tone: 'neutral',
    detail: 'Choose a plan below and send it for approval.',
  },
  pending: {
    label: 'Awaiting approval',
    tone: 'warning',
    detail: 'Your request has been sent. You will be able to use the platform as soon as it is approved.',
  },
  active: {
    label: 'Active',
    tone: 'success',
    detail: 'Your subscription is active.',
  },
  rejected: {
    label: 'Not approved',
    tone: 'danger',
    detail: 'Your request was not approved. You can send another one.',
  },
  suspended: {
    label: 'Suspended',
    tone: 'danger',
    detail: 'Your subscription has been suspended. Please get in touch.',
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'danger',
    detail: 'Your subscription has been cancelled.',
  },
};
